import React, { FC, useEffect, useState } from 'react';
import { Box, Button, Chip, Divider, LinearProgress, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SurveyService from '../../services/SurveyService';
import { WidgetNoData } from './WidgetNoData';
import SurveyUtils from '../../shared/SurveyUtils';

interface ISurveyData {
  id: string;
  label: string;
  type: string;
  participantsCount: number;
  createdAt: string;
  day: number;
  week: number;
  month: number;
}

/**
 * Widget component that displays survey data with filtering capabilities.
 * Shows a list of surveys with their participant counts and allows filtering by time period.
 */
const WidgetSurveyTool: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ISurveyData[]>([]);
  const [timeFilter, setTimeFilter] = useState(() => {
    // Load saved filter from localStorage or use default 'month'
    const savedFilter = localStorage.getItem('repWidgetSurveyToolFilter');
    return savedFilter || 'month';
  });

  useEffect(() => {
    const controller = new AbortController();

    const getSurveyData = async () => {
      try {
        const response = await SurveyService.getSurvey(setLoading, controller.signal);
        if (response) {
          const participants = await SurveyService.getSurveyParticipants(controller.signal);
          const bySurvey = new Map(
            (participants || []).map((participant) => [participant.surveyId, participant])
          );
          const surveysWithAnswers = response.map((survey: any) => {
            const participant = bySurvey.get(survey.id);
            return {
              id: survey.id,
              label: survey.label,
              type: survey.type,
              participantsCount: participant?.month || 0,
              createdAt: survey.createdAt,
              day: participant?.day || 0,
              week: participant?.week || 0,
              month: participant?.month || 0,
            };
          });

          setData(surveysWithAnswers);
          setLoading(false);
        }
      } catch (error) {
        console.error('Error fetching survey data:', error);
      } finally {
        setLoading(false);
      }
    };

    getSurveyData();

    return () => {
      controller.abort();
    };
  }, []);

  const handleTimeFilterChange = (value: string) => {
    setTimeFilter(value);
    // Save filter preference to localStorage
    localStorage.setItem('repWidgetSurveyToolFilter', value);
  };

  /**
   * Filters survey data based on selected time period (day, week, month)
   * and returns updated survey objects with filtered answers
   */
  const getFilteredData = () => {
    return data.map((survey) => {
      return {
        ...survey,
        participantsCount:
          timeFilter === 'day' ? survey.day : timeFilter === 'week' ? survey.week : survey.month,
      };
    });
  };

  /**
   * Converts survey type to localized display string
   * @param type - The type of survey to translate
   * @returns Translated survey type string
   */
  const getTranslatedType = (type: string) => {
    return SurveyUtils.getTypeName(type, t);
  };

  const filteredData = getFilteredData().filter((survey) => survey.participantsCount > 0);

  return (
    <Box>
      {loading ? (
        <Box>
          <LinearProgress sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'right', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                size="small"
                variant={timeFilter === 'day' ? 'contained' : 'outlined'}
                onClick={() => handleTimeFilterChange('day')}
                sx={{ py: 0, px: 0.5, minWidth: '75px', fontSize: '0.75rem' }}
              >
                {t('widget_surveytool_filter_day')}
              </Button>
              <Button
                size="small"
                variant={timeFilter === 'week' ? 'contained' : 'outlined'}
                onClick={() => handleTimeFilterChange('week')}
                sx={{ py: 0, px: 0.5, minWidth: '75px', fontSize: '0.75rem' }}
              >
                {t('widget_surveytool_filter_week')}
              </Button>
              <Button
                size="small"
                variant={timeFilter === 'month' ? 'contained' : 'outlined'}
                onClick={() => handleTimeFilterChange('month')}
                sx={{ py: 0, px: 0.5, minWidth: '75px', fontSize: '0.75rem' }}
              >
                {t('widget_surveytool_filter_month')}
              </Button>
            </Box>
          </Box>
          <Divider sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
          <Box sx={{ maxHeight: 230, overflow: 'auto' }}>
            {filteredData.length > 0 ? (
              filteredData.map((survey) => (
                <Box
                  key={survey.id}
                  sx={{
                    mb: 1,
                    p: 1,
                    borderRadius: 1,
                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '1rem',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle2">{survey.label}</Typography>
                      <Chip
                        size="small"
                        label={getTranslatedType(survey.type)}
                        sx={{ ml: theme.spacing(1), height: '20px', fontSize: '0.75rem' }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', textWrap: 'nowrap' }}>
                      <Typography
                        variant="body1"
                        color={theme.palette.primary.main}
                        fontWeight="bolder"
                      >
                        {survey.participantsCount}&nbsp;
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        {t('widget_surveytool_participants')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))
            ) : (
              <WidgetNoData />
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default WidgetSurveyTool;
